// @flow
import { get } from '@rover/react-lib/apis/currentUser.api';
// $FlowFixMe flow does not resolve .native.js correctly for RN
import logout from '@rover/react-lib/apis/logout.api';
import fetchCurrentUserResponseFactory from '@rover/react-lib/factories/fetchCurrentUserResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/lib/redux-duck';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type { FetchCurrentUserResponse, User, UtmParams } from '@rover/react-lib/types';
import reportError from '@rover/utilities/reportError';
import { configureScope } from '@rover/utilities/sentry';

import generateActions from './generateActions';

export type State = {
  errorMessage: string,
  loading: boolean,
  logoutPending: boolean,
  fetchCurrentUserResponse: FetchCurrentUserResponse,
};

type Actions = {
  fetchCurrentUserPending: ActionCreator<void>,
  fetchCurrentUserSuccess: ActionCreator<FetchCurrentUserResponse>,
  fetchCurrentUserFailure: ActionCreator<string>,
  logoutCurrentUserPending: ActionCreator<void>,
  logoutCurrentUserSuccess: ActionCreator<FetchCurrentUserResponse>,
  logoutCurrentUserFailure: ActionCreator<string>,
};

type Effects = {
  fetchCurrentUser: EffectCreator<void>,
  logoutCurrentUser: EffectCreator<{ nextLocation: string, ref?: string }>,
  initSentryUser: EffectCreator<void>,
};

type Selectors = {
  getCurrentUser: SelectorCreator<User>,
  getRealUser: SelectorCreator<User>,
  isLoading: SelectorCreator<boolean>,
  getErrorMessage: SelectorCreator<string>,
  getApiUrl: SelectorCreator<string>,
  getOpk: SelectorCreator<string>,
  isAuthenticated: SelectorCreator<boolean>,
  isImpersonating: SelectorCreator<boolean>,
  getPeopleUrl: SelectorCreator<string>,
  isNewCustomer: SelectorCreator<boolean>,
  getPostalCode: SelectorCreator<string>,
  getUtmParams: SelectorCreator<UtmParams>,
  getFirstHttpReferrer: SelectorCreator<string>,
  getIsSsuMigrated: SelectorCreator<boolean>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'currentUser',
  initialState: {
    errorMessage: '',
    loading: true,
    logoutPending: false,
    fetchCurrentUserResponse: fetchCurrentUserResponseFactory(),
  },
  // $FlowFixMe
  actions: {
    ...generateActions('fetchCurrentUser'),
    ...generateActions('logoutCurrentUser'),
  },
  effects: {
    fetchCurrentUser: () => async (
      dispatch,
      getState,
      { fetchCurrentUserPending, fetchCurrentUserSuccess, fetchCurrentUserFailure },
      { initSentryUser },
      selectors,
      { expressParams }
    ) => {
      dispatch(fetchCurrentUserPending());
      try {
        const fetchCurrentUserResponse: FetchCurrentUserResponse = await get(expressParams);
        dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponse));
        dispatch(initSentryUser());
      } catch (e) {
        if (e.status === 404) {
          dispatch(fetchCurrentUserSuccess(fetchCurrentUserResponseFactory()));
        } else {
          dispatch(fetchCurrentUserFailure(e.message));
          reportError(e);
        }
      }
    },
    logoutCurrentUser: ({ nextLocation, ref }) => async (
      // Only call this on the client, never the server
      dispatch,
      getState,
      { logoutCurrentUserPending, logoutCurrentUserSuccess, logoutCurrentUserFailure }
    ) => {
      dispatch(logoutCurrentUserPending());
      try {
        logout({
          nextLocation,
          ref,
          beforeNavCallback: () =>
            dispatch(logoutCurrentUserSuccess(fetchCurrentUserResponseFactory())),
        });
      } catch (e) {
        dispatch(logoutCurrentUserFailure(e.message));
        reportError(e);
      }
    },
    initSentryUser: () => async (dispatch, getState, actions, effects, { getOpk }) => {
      configureScope((scope) => scope.setUser({ id: getOpk(getState()) }));
    },
  },
  selectors: {
    getCurrentUser: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCurrentUserResponse.currentUser),
    getRealUser: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCurrentUserResponse.realUser),
    isLoading: (getState, createSelector) => createSelector([getState], (x) => x.loading),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (x) => x.errorMessage),
    getApiUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.apiUrl),
    getOpk: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.opk),
    isAuthenticated: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchCurrentUserResponse.authenticated),
    isImpersonating: (getState, createSelector, { getRealUser }) =>
      createSelector([getRealUser], (x) => !!x.opk.length),
    getPeopleUrl: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => `/admin/people/person/?slug=${x.slug}`),
    isNewCustomer: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isNewCustomer),
    getPostalCode: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.postalCode),
    getUtmParams: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.session && x.session.utmParameters),
    getFirstHttpReferrer: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.session && x.session.firstHttpReferrer),
    getIsSsuMigrated: (getState, createSelector, { getCurrentUser }) =>
      createSelector([getCurrentUser], (x) => x.isSsuMigrated),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/currentUser.duck.js